/**
 * GZOO AI Marketing - Frontend
 * Shared auth-session plumbing for every axios client.
 *
 * Three jobs the per-client interceptors used to get wrong (or not do at all):
 *   1. Single-flight token refresh — a burst of concurrent 401s triggers exactly ONE
 *      /auth/refresh, not a stampede. Refresh tokens ROTATE server-side, so uncoordinated
 *      parallel refreshes race the rolling window and can spuriously log the user out.
 *   2. Transient-network retry — a dropped connection (net::ERR_CONNECTION_CLOSED, reset socket)
 *      produces NO http response, so the 401 path never fires and the user just sees a raw
 *      "network error". We retry such a request ONCE. By default only safe READ methods
 *      (GET/HEAD/OPTIONS) auto-retry. A mutation must EXPLICITLY opt in via
 *      `config.retryOnNetworkError = true`, and may only do so when the endpoint is idempotent —
 *      because a dropped socket can occur AFTER the server already committed the write, so the
 *      retry can re-apply it. A create/launch/charge (POST/PATCH) is therefore never silently
 *      re-sent; the spend-cap / boost settings PUTs opt in because they write ABSOLUTE values.
 *   3. Impersonation-safe forced logout — if only an impersonated tenant session dies, drop the
 *      operator back into their own (still-valid) session; a real logout clears ALL keys
 *      including op_backup_* so a stale backup can't corrupt the next "enter tenant".
 *
 * Storage keys MUST stay in sync with hooks/useAuth.ts and lib/impersonation.ts.
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api/v1';

const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';
// Operator-impersonation keys (mirror lib/impersonation.ts). Inlined as literals rather than
// imported to avoid an import cycle: impersonation.ts -> api/platform -> api/client -> authSession.
const OP_BACKUP_ACCESS_KEY = 'op_backup_access_token';
const OP_BACKUP_REFRESH_KEY = 'op_backup_refresh_token';
const IMPERSONATING_KEY = 'impersonating_tenant';

// Every key that holds auth state. A full logout clears all of them — leaving a stale op_backup_*
// behind would make the next enterTenant() skip its backup guard and a later exit restore the
// WRONG operator tokens.
const ALL_AUTH_KEYS = [
  ACCESS_KEY,
  REFRESH_KEY,
  OP_BACKUP_ACCESS_KEY,
  OP_BACKUP_REFRESH_KEY,
  IMPERSONATING_KEY,
];

const SESSION_EXPIRED_MSG = 'Your session has expired. Please log in again.';

// Read methods are always safe to re-fire on a pure connection drop.
const AUTO_RETRY_METHODS = new Set(['get', 'head', 'options']);

/**
 * Axios config augmented with an opt-in for transient-network retry on a non-read method. Only
 * set `retryOnNetworkError: true` on a request the caller has verified to be server-idempotent
 * (re-applying it yields identical state, with no extra side effects).
 */
export interface RetryableRequestConfig extends AxiosRequestConfig {
  retryOnNetworkError?: boolean;
}

const isBrowser = () => typeof window !== 'undefined';
const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function getAccessToken(): string | null {
  return isBrowser() ? localStorage.getItem(ACCESS_KEY) : null;
}

/** Remove every auth key (active session + operator impersonation backups). No redirect. */
export function clearAuthStorage(): void {
  if (!isBrowser()) return;
  for (const key of ALL_AUTH_KEYS) localStorage.removeItem(key);
}

// ---------------------------------------------------------------- single-flight refresh
let inFlightRefresh: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  if (!isBrowser()) return null;
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;
  try {
    // Bare axios (NOT an intercepted instance) so a 401 from /auth/refresh can't recurse back
    // into this same refresh logic.
    const resp = await axios.post(
      `${API_BASE_URL}/auth/refresh`,
      { refresh_token: refreshToken },
      { timeout: 15000 },
    );
    const access: string | undefined = resp.data?.access_token;
    const rotated: string | undefined = resp.data?.refresh_token;
    if (!access) return null;
    localStorage.setItem(ACCESS_KEY, access);
    if (rotated) localStorage.setItem(REFRESH_KEY, rotated); // tokens rotate — store the new one
    return access;
  } catch {
    return null;
  }
}

/**
 * Refresh the access token, collapsing concurrent callers onto a single /auth/refresh request.
 * Resolves to the new access token, or null if there is no refresh token / the refresh failed.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!inFlightRefresh) {
    inFlightRefresh = performRefresh().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

// ---------------------------------------------------------------- forced logout
let loggingOut = false;

/**
 * Tear down a dead session. If an operator is impersonating and only the tenant session died,
 * restore the operator's own (still-valid) session instead of logging them out entirely.
 * Otherwise clear all auth state and bounce to /login. Guarded so a burst of failures fires a
 * single navigation; the guard self-heals after a few seconds in case the navigation is somehow
 * prevented, so a later genuine logout can still fire.
 */
export function forceLogout(message?: string): void {
  if (!isBrowser() || loggingOut) return;
  loggingOut = true;
  setTimeout(() => {
    loggingOut = false;
  }, 5000);

  const backupAccess = localStorage.getItem(OP_BACKUP_ACCESS_KEY);
  if (localStorage.getItem(IMPERSONATING_KEY) && backupAccess) {
    // Only the impersonated tenant session expired — return to the operator account (mirrors
    // lib/impersonation.ts exitImpersonation) rather than logging the operator out.
    const backupRefresh = localStorage.getItem(OP_BACKUP_REFRESH_KEY);
    localStorage.setItem(ACCESS_KEY, backupAccess);
    if (backupRefresh) localStorage.setItem(REFRESH_KEY, backupRefresh);
    else localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(OP_BACKUP_ACCESS_KEY);
    localStorage.removeItem(OP_BACKUP_REFRESH_KEY);
    localStorage.removeItem(IMPERSONATING_KEY);
    window.location.href = '/admin/tenants';
    return;
  }

  clearAuthStorage();
  if (message) {
    // eslint-disable-next-line no-alert
    alert(message);
  }
  window.location.href = '/login';
}

// ---------------------------------------------------------------- interceptors
type RetriableConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _netRetry?: boolean;
  retryOnNetworkError?: boolean;
};

/** A network-layer failure with no HTTP response (dropped/closed connection, DNS blip). */
function isTransientNetworkError(error: AxiosError): boolean {
  // ERR_CANCELED = caller aborted on purpose; ECONNABORTED = our own timeout (don't double the
  // wait on a genuinely slow save). Everything else with no response is a real transient drop.
  return !error.response && error.code !== 'ERR_CANCELED' && error.code !== 'ECONNABORTED';
}

/**
 * Install the shared request (attach bearer) + response (refresh / retry / logout) interceptors
 * on an axios instance. Safe for any client that authenticates via the access_token in
 * localStorage.
 */
export function installAuthInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use(
    (config) => {
      const token = getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetriableConfig | undefined;
      if (!originalRequest) return Promise.reject(error);

      // 1) Transient connection drop (no HTTP response) — retry ONCE. Reads auto-retry; a mutation
      //    only retries if it explicitly opted in (it is server-idempotent).
      const method = (originalRequest.method || 'get').toLowerCase();
      const mayRetryNetwork =
        AUTO_RETRY_METHODS.has(method) || originalRequest.retryOnNetworkError === true;
      if (isTransientNetworkError(error) && mayRetryNetwork && !originalRequest._netRetry) {
        originalRequest._netRetry = true;
        await delay(600);
        return instance(originalRequest);
      }

      // 2) 401 — access token expired/invalid. Refresh (single-flight) and retry once.
      const isAuthEndpoint = originalRequest.url?.includes('/auth/');
      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        originalRequest._retry = true;
        // The auth retry re-delivers the request body; forbid a *subsequent* network-retry of that
        // same delivery so a mutation can never be dispatched a third time on a later blip.
        originalRequest._netRetry = true;

        // If a concurrent request already refreshed the token while we were waiting, reuse it
        // instead of kicking off another refresh.
        const sent = originalRequest.headers?.Authorization;
        const current = getAccessToken();
        const newAccess =
          current && sent !== `Bearer ${current}` ? current : await refreshAccessToken();

        if (newAccess) {
          originalRequest.headers.Authorization = `Bearer ${newAccess}`;
          try {
            return await instance(originalRequest);
          } catch (retryError) {
            // Still 401 after a fresh token => the session is genuinely dead.
            if ((retryError as AxiosError).response?.status === 401) {
              forceLogout(SESSION_EXPIRED_MSG);
            }
            return Promise.reject(retryError);
          }
        }

        // No refresh token, or the refresh itself failed.
        forceLogout(SESSION_EXPIRED_MSG);
        return Promise.reject(error);
      }

      return Promise.reject(error);
    },
  );
}
